import type { AttentionPort } from '@elara/client-core/lib/attention-port';

/**
 * Web `AttentionPort`: the guest is looking at Elara when the tab is visible AND its window has focus.
 *
 * Visibility alone is not enough: a visible tab behind another app's window is exactly when a
 * new-chat-message push should still fire. `pagehide` reports a non-attentive, unloading change so
 * the last presence report can go out with fetch `keepalive`.
 *
 * Missing globals (SSR, locked-down contexts) degrade to always attentive with no changes.
 *
 * This file is web-only and must not be imported by `@elara/mobile`.
 */
export function createWebAttentionPort(): AttentionPort {
  const isAttentive = (): boolean =>
    typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus();

  return {
    isAttentive() {
      if (typeof document === 'undefined') return true;
      return isAttentive();
    },
    onChange(listener) {
      if (typeof document === 'undefined' || typeof window === 'undefined') return () => {};

      let last = isAttentive();
      const handleChange = () => {
        const next = isAttentive();
        if (next === last) return;
        last = next;
        listener(next, { unloading: false });
      };
      const handlePageHide = () => {
        last = false;
        listener(false, { unloading: true });
      };

      document.addEventListener('visibilitychange', handleChange);
      window.addEventListener('focus', handleChange);
      window.addEventListener('blur', handleChange);
      window.addEventListener('pagehide', handlePageHide);

      return () => {
        document.removeEventListener('visibilitychange', handleChange);
        window.removeEventListener('focus', handleChange);
        window.removeEventListener('blur', handleChange);
        window.removeEventListener('pagehide', handlePageHide);
      };
    },
  };
}
